import { OmniaCommandGroup } from "@omnia/shared/components/omniaCommandGroup";
import { CropPurpose } from "@omnia/shared/generated/enums";
import { labels } from "@omnia/shared/generated/labels";
import { groupBy, sortBy } from "@omnia/shared/utils/array";
import { hectareFormat } from "@omnia/shared/utils/format";
import { nanoid } from "nanoid";
import {
    getCroppingCommands,
    getCroppingCommandsForDetailBox,
    getFieldCommands,
    getFieldGroupCommands,
} from "../commands";
import { SetupCropYearCmd } from "../cropYear/setupCropYearCmd";
import { DisableCmd } from "../disableFields/disableCmd";
import { CreateFieldsCmd, EditCmd } from "../edit/editCmd";
import {
    components,
    createFilter,
    defineComponent,
    FieldViewModel,
    ICrop,
    IOmniaCommandGroup,
    IOmniaSettings,
    IOmniaShell,
    isNumber,
    IVariety,
    LocaleString,
    shell,
    str,
    watchFields,
} from "../omnia";
import { EditCmd as StylesEditCmd } from "../styles/editCmd";
import { IFieldsMapCroppingDetailsContext, IFieldsModuleContext, IFieldsService } from "../types";

export function createMenu(): ng.IDirective {
    return defineComponent(FieldsMenuCtrl, "app/fields/menu/menu.html", {
        context: "=",
    });
}

type FieldsMenuGroupMode = "field" | "fieldGroup" | "crop";

/**
 * Generate a random ID to represent a node.
 * @returns A random ID to represent a node.
 */
const nodeId = () => nanoid(6);

interface Node<T> {
    data: T;
    /**
     * A unique ID for the current node.
     *
     * (This should be unique across all nodes in a given tree)
     */
    id: string;
    /**
     * The ID of the parent node. (if any)
     */
    parent?: string;
    children?: Node<any>[];
    onSelect?: () => void;
    onEdit?: () => void;
}

type FieldNode = Node<FieldViewModel> & {
    dataType: "field";
};

type FieldGroupNode = Node<string> & {
    dataType: "fieldGroup";
};

type CroppingNode = Node<FieldViewModel> & {
    dataType: "cropping";
};

type CropNode = Node<ICrop> & {
    dataType: "crop";
};

type PurposeNode = Node<CropPurpose> & {
    dataType: "purpose";
};

type VarietyNode = Node<IVariety> & {
    dataType: "variety";
};

type TypedNode = FieldNode | FieldGroupNode | CroppingNode | CropNode | PurposeNode | VarietyNode;

export class FieldsMenuCtrl extends shell.Component {
    private context: IFieldsModuleContext;

    private fieldGroupCommands: IOmniaCommandGroup;
    private fieldCommands: IOmniaCommandGroup;
    private croppingCommands: IOmniaCommandGroup;

    private fields: FieldViewModel[];

    private _settings: IOmniaSettings;

    static $inject = ["$scope", "OmniaFieldsService", "OmniaShell"];

    private groupModeOptions: {
        mode: FieldsMenuGroupMode;
        label: LocaleString;
        icon?: string;
        disabled?: boolean;
    }[];

    private _groupMode: (typeof this.groupModeOptions)[0] = undefined;

    get groupMode() {
        return this._groupMode;
    }

    set groupMode(value) {
        this._groupMode = value;
        this._settings.getSettings("menu").set("groupMode", this._groupMode.mode);
        this.expandedNodeIds.clear();
        this.rootNodes = this.getRootNodes(this.fields, this._groupMode?.mode);
    }

    /**
     * The root nodes of the current tree.
     */
    public rootNodes: TypedNode[];

    public readonly expandedNodeIds: Set<string> = new Set<string>();

    public readonly selectedNodeIds: Set<string> = new Set<string>();

    constructor(
        $scope: ng.IScope,
        private svc: IFieldsService,
        private shell: IOmniaShell,
        private session = shell.getSession()
    ) {
        super($scope, ["vm.context"]);

        this._icon = "icon-holding-fields";
        this._title = str.Modules.Fields;
        this._pinnable = true;
        this._filter = createFilter(this.svc);
        this._settings = this.shell.getSession().getSettings("fields");
    }

    $onInit() {
        this.setupCommands();

        watchFields(this.svc, this.$scope, (f) => this.setupFields(f), true).then(() => {
            this.$scope.$watch("vm.svc.selectedItems", () => this.synchronizeSelection());
            this.startInitAction();
        });
    }

    private startInitAction() {
        const params = this.shell.getRouteParams();

        const { action, id } = params;

        if (!action) return;

        switch (params["action"]) {
            case "edit-cropping":
                if (id) {
                    const c = this.svc.findCropping(id);
                    const cmd = this.croppingCommands.commands.find((c) => c instanceof EditCmd && c.editCroppings);
                    if (c && cmd) {
                        cmd.execute(c);
                    }
                }
                break;

            case "edit-field":
                if (id) {
                    const f = this.svc.findField(id);
                    const cmd = this.fieldCommands.commands.find((c) => c instanceof EditCmd && c.editFields);
                    if (f && cmd) {
                        cmd.execute(f);
                    }
                }
                break;

            case "edit-styles": {
                const cmd = new StylesEditCmd(this.svc, this.shell);
                cmd.execute();
                break;
            }
        }
    }

    private setupFields(fields: FieldViewModel[]) {
        this.fields = fields;

        let fieldGroups = this.calculateFieldGroups(this.fields);
        this.groupModeOptions = this.calculateGroupModeOptions(fieldGroups);

        // Determine the initial group mode.
        const lastGroupMode: FieldsMenuGroupMode | undefined = this._settings
            .getSettings("menu")
            .get("groupMode", undefined);
        // The option saved might no longer be a valid option.
        const validGroupModeOptions = this.groupModeOptions.filter((x) => !x.disabled);
        this.groupMode ??= validGroupModeOptions.find((x) => x.mode === lastGroupMode) ?? validGroupModeOptions[0];

        this.rootNodes = this.getRootNodes(this.fields, this._groupMode?.mode);

        this.synchronizeSelection();

        this.setupDetailsBox();
    }

    get currentYear(): number {
        return this.svc.yearId;
    }

    /**
     *
     * @param nodeId
     */
    expandSelfAndAncestors(nodeId: string) {
        const expand = (id: string) => {
            const node = this.filterNodes((x) => x.id === id)?.[0];
            if (node) {
                if (node.children?.length) {
                    this.expandedNodeIds.add(node.id);
                }

                if (node.parent) {
                    expand(node.parent);
                }
            }
        };

        expand(nodeId);
    }

    setSelectedNodes(nodes: Node<any>[], expandAncestors: boolean = false) {
        // Keep a copy of the previous selection.
        const prev = new Set(this.selectedNodeIds);
        // Get the new node IDs
        const next = new Set(nodes.map((node) => node.id));
        // Clear the current selection.
        this.selectedNodeIds.clear();
        // Select each node.
        next.forEach((id) => this.selectedNodeIds.add(id));

        if (expandAncestors) {
            // Expand all parents of nodes, that weren't already selected before we synced
            nodes.filter((node) => !prev.has(node.id)).forEach((node) => this.expandSelfAndAncestors(node.id));
        }

        return {
            prev,
            difference: prev.symmetricDifference(next),
            added: next.difference(prev),
            next,
        };
    }

    findFieldNodes(f: FieldViewModel): TypedNode[] {
        // Find all nodes that represent this field.
        return this.filterNodes((x) => x.dataType == "field" && x.data.fieldId === f.fieldId);
    }

    findCroppingNodes(c: FieldViewModel): TypedNode[] {
        // Find all nodes that represent this cropping.
        return this.filterNodes((x) => x.dataType == "cropping" && x.data.croppingId === c.croppingId);
    }

    selectField(f: FieldViewModel, expandAncestors: boolean = false, scrollIntoView: boolean = false) {
        // Find all nodes that represent this field.
        const nodes = this.findFieldNodes(f);

        // If all of those nodes are already selected, this is a no-op.
        if (nodes.every((node) => this.selectedNodeIds.has(node.id))) {
            return;
        }

        // Select those nodes.
        this.setSelectedNodes(nodes, expandAncestors);
        // Select the item in the field service.
        this.selectItems([f]);

        if (scrollIntoView) {
            this.scrollToNodes(nodes);
        }
    }

    selectCropping(c: FieldViewModel, expandAncestors: boolean = false, scrollIntoView: boolean = false) {
        // Find all nodes that represent this cropping.
        const nodes = this.findCroppingNodes(c);

        // If all of those nodes are already selected, this is a no-op.
        if (nodes.every((node) => this.selectedNodeIds.has(node.id))) {
            return;
        }

        // Select those nodes.
        this.setSelectedNodes(nodes, expandAncestors);
        // Select the item in the field service.
        this.selectItems([c]);

        if (scrollIntoView) {
            this.scrollToNodes(nodes);
        }
    }

    private scrollToNodes(nodes: Node<any>[]) {
        this.scrollToNodeById(nodes?.[0]?.id);
    }

    private scrollToNodeById(nodeId: string) {
        if (!nodeId) return;

        // If we're not active, we can't scroll.
        if (!this.isActive()) return;

        this.shell.timeout().then(() =>
            super.scrollItemIntoView(`[data-node-id='${nodeId}']`, {
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            })
        );
    }

    private selectItems(items: FieldViewModel[]) {
        this.svc.selectItems(items, true);
    }

    private synchronizeSelection() {
        // Get the items selected on the map.
        var selectedItems = this.svc.selectedItems;
        // Find the nodes which correspond to the selection.
        var nodes = selectedItems
            .flatMap((item) =>
                item.isField() ? this.findFieldNodes(item) : item.isCropping() ? this.findCroppingNodes(item) : []
            )
            .unique((x) => x.id);
        // Mark those nodes as selected on the menu.
        const selection = this.setSelectedNodes(nodes, true);
        // If the selection has changed, then scroll to it.
        if (selection.difference.size) {
            // If we've just selected a new node, scroll to that. Otherwise, scroll to the first node in the selection.
            this.scrollToNodeById([...selection.added][0] ?? [...selection.next][0]);
        }
    }

    locateItems(items: FieldViewModel[]) {
        this.svc.locateItems(items);
    }

    startComponent(options: shell.IComponentStartOptions): shell.IComponentContext {
        return this.componentContext.startComponent(options);
    }

    private setupCommands() {
        var ctxt = this.context;

        this.commands = [
            new CreateFieldsCmd(this.context),
            new SetupCropYearCmd(this.svc, this.shell),
            new DisableCmd(this.svc, this.shell),
            new StylesEditCmd(this.svc, this.shell),
        ];

        this.fieldGroupCommands = new components.OmniaCommandGroup(
            str.FieldGroup,
            undefined,
            getFieldGroupCommands(ctxt)
        );

        this.croppingCommands = new components.OmniaCommandGroup(
            str.CroppingPlural,
            undefined,
            getCroppingCommands(ctxt, false)
        );

        this.fieldCommands = new components.OmniaCommandGroup(
            str.Field,
            undefined,
            getFieldCommands(ctxt, false, true)
        );
    }

    getCropColor(fieldVm: FieldViewModel) {
        return fieldVm?.cssColor;
    }

    /**
     * Checks if the crop circle color is light
     * @param fieldVm The field to check.
     * @returns `true` if the crop circle should be displayed with a border
     */
    getCropCircleRequiresBorder(fieldVm: FieldViewModel) {
        const color = this.getCropColor(fieldVm)?.toLowerCase();
        return !color || ["fff", "ffffff", "transparent"].includes(color);
    }

    private calculateFieldGroups(fields: FieldViewModel[]): string[] {
        return [...new Set(fields.map((x) => x.fieldGroup).filter(Boolean))].sort();
    }

    private calculateGroupModeOptions(fieldGroups: string[]): typeof this.groupModeOptions {
        const options: typeof this.groupModeOptions = [
            {
                mode: "field",
                label: str.Field,
                icon: "icon icon-holding-fields",
            },
            {
                mode: "fieldGroup",
                label: str.FieldGroup,
                icon: "fas fa-layer-group",
                disabled: !fieldGroups.length,
            },
            {
                mode: "crop",
                label: str.Crop,
                icon: "icon icon-cropping",
            },
        ];

        return options.filter((x) => !x.disabled);
    }

    /**
     * Generates the tree and returns an array of root nodes, depending on the current `this.groupMode`
     * @param fields The fields to transform into a tree of nodes.
     * @returns The list of root nodes for the tree.
     */
    private getRootNodes(fields: FieldViewModel[], groupMode?: FieldsMenuGroupMode) {
        switch (groupMode) {
            case "field":
                return this.getRootNodesForFields(fields);
            case "fieldGroup":
                return this.getRootNodesForFieldGroups(fields);
            case "crop":
                return this.getRootNodesForCrops(fields);
            default:
                return [];
        }
    }

    // NOTE - BEN: These grouped-tree-node methods are ugly and should probably be re-written with reduce/curry.
    private getRootNodesForFields(fields: FieldViewModel[]) {
        return sortBy(fields, ["disabled", "fieldName", "fieldId"])
            .filter((x) => x.visible)
            .map((x) => {
                const visibleCroppings = x.croppings.filter((x) => x.visible && x.currentYear);

                return withChildren(
                    {
                        dataType: "field",
                        data: x,
                        id: nodeId(),
                        onSelect: () => this.selectField(x),
                    },
                    maybeGroupCrop(visibleCroppings, (items) =>
                        maybeGroupVariety(items, (items) =>
                            asCroppingNodes(items, (cropping) => this.selectCropping(cropping))
                        )
                    )
                );
            });
    }

    // NOTE - BEN: These grouped-tree-node methods are ugly and should probably be re-written with reduce/curry.
    private getRootNodesForFieldGroups(fields: FieldViewModel[]): FieldGroupNode[] {
        const groups = groupBy(this.getRootNodesForFields(fields), (x) => x.data.fieldGroup).map<FieldGroupNode>((g) =>
            withChildren(
                {
                    dataType: "fieldGroup",
                    id: nodeId(),
                    data: g.key,
                },
                sortBy(g.values, [(x) => x.data?.fieldName, (x) => x.data?.fieldId])
            )
        );
        return sortBy(groups, ["data"]);
    }

    // NOTE - BEN: These grouped-tree-node methods are ugly and should probably be re-written with reduce/curry.
    private getRootNodesForCrops(fields: FieldViewModel[]): TypedNode[] {
        // Get all croppings which should appear.
        const croppings = fields
            .flatMap((x) => x.croppings)
            .filter((x) => x.visible && x.currentYear && !x.fieldVm.disabled);

        return maybeGroupCrop(croppings, (items) =>
            maybeGroupPurpose(items, (items) =>
                maybeGroupVariety(items, (items) =>
                    groupByField(items).map<FieldNode>((field) =>
                        withChildren(
                            {
                                dataType: "field",
                                data: field.key,
                                id: nodeId(),
                                onSelect: () => this.selectField(field.key),
                            },
                            asCroppingNodes(field.values, (cropping) => this.selectCropping(cropping))
                        )
                    )
                )
            )
        );
    }

    getLabelForNode(node: TypedNode): string {
        // Determine the total area of the current node.
        const totalArea =
            typeof node.data === "object" && isNumber((node.data as any).area) ? (node.data as any).area : null;
        const withArea = (str: string) => (totalArea ? `${str} (${hectareFormat.format(totalArea)})` : str);

        switch (node.dataType) {
            case "crop":
                return withArea(node?.data?.Name ?? "Uncropped");
            case "field":
                return withArea(node?.data?.fieldName ?? "(no field)");
            case "cropping":
                return node?.data?.getDescription(true) ?? "(no cropping)";
            case "fieldGroup":
                return withArea(node?.data ?? "(no field group)");
            case "purpose":
                return withArea(node.data ? labels.CropPurpose(node.data) : "(no purpose)");
            case "variety":
                return withArea(node?.data?.Name ?? "(no variety)");
            default:
                return "(unknown node)";
        }
    }

    /**
     * Toggles ALL nodes expanded/collapsed.
     * @param state The desired state.
     * @param recursive Whether to recursively toggle children to match.
     */
    toggleAllNodes(state?: boolean, recursive: boolean = true) {
        // Determine next state.
        state ??= !this.rootNodes.some((node) => this.nodeHasExpandedDescendants(node));

        // Apply to all root nodes.
        this.rootNodes.forEach((node) => this.toggleNode(node, state, recursive));
    }

    /**
     * Toggles whether the selected node is expanded/collapsed.
     * @param node The node to toggle.
     * @param state The desired state.
     * @param recursive Whether to recursively toggle children to match.
     * @param event
     */
    toggleNode(node: TypedNode, state?: boolean, recursive?: boolean, event?: angular.IAngularEvent) {
        if (!node || !node.id) return;

        event?.stopPropagation();

        // Determine next state.
        state ??= !this.expandedNodeIds.has(node.id);

        // Toggle self, if we have children.
        if (state && node.children) {
            this.expandedNodeIds.add(node.id);
        } else {
            this.expandedNodeIds.delete(node.id);
        }

        // If told to recurse, continue with children.
        // Also, if the node has exactly one child, expand that child automatically.
        if (recursive || (node.children?.length === 1 && state)) {
            node.children?.forEach((child) => this.toggleNode(child as TypedNode, state, recursive));
        }
    }

    /**
     * Toggles whether nodes matching the `predicate` are expanded/collapsed.
     * @param predicate The predicate to match nodes against.
     * @param state The desired state.
     * @param recursive Whether to recursively toggle children to match.
     */
    toggleNodesByPredicate(
        predicate: (value: TypedNode, index: number, array: TypedNode[]) => boolean,
        state?: boolean,
        recursive?: boolean
    ) {
        // Find nodes.
        const nodes = this.filterNodes(predicate);

        // Determine next state.
        state ??= !nodes.some((x) => this.expandedNodeIds.has(x.id));

        // Toggle the nodes accordingly.
        for (const node of nodes) {
            this.toggleNode(node, state, recursive);
        }
    }

    /**
     * Selects the specified node (if possible, not all node types can be selected)
     * @param node The node to select.
     * @returns
     */
    selectNode(node: TypedNode) {
        if (!node || !node.id) return;

        switch (node.dataType) {
            case "field":
                this.selectField(node.data);
                break;
            case "cropping":
                this.selectCropping(node.data);
                break;
        }
    }

    /**
     * Handle when a node is clicked from the menu
     * @param node The node being
     * @returns 
     */
    onNodeClick(node: TypedNode) {
        if (!node || !node.id) return;

        // Not all nodes are "selectable".
        const selectable = node.dataType === "field" || node.dataType === "cropping";

        // What's the current state of the node?
        const selected = this.isNodeSelected(node);
        const next = !this.isNodeExpanded(node);

        this.selectNode(node);

        this.toggleNode(node, (selectable && !selected) || next);
    }

    /**
     * Whether the node, or any of its descendants, are expanded.
     * @param node The node to check.
     * @returns `true` if the specified node, or any of its descendants, are expanded.
     */
    nodeHasExpandedDescendants(node: Node<any>) {
        if (!node) return false;

        return this.getSelfAndDescendants(node).some((x) => this.expandedNodeIds.has(node.id));
    }

    /**
     * Gets a flat array of all nodes in the current tree.
     * @returns An array of all nodes in the current tree.
     */
    getAllNodes(): typeof this.rootNodes {
        return this.rootNodes.reduce((arr, node) => [...arr, ...this.getSelfAndDescendants(node)], []);
    }

    /**
     * Finds nodes that match the predicate condition.
     * @param predicate A predicate to match nodes against.
     * @returns
     */
    filterNodes<T extends (typeof this.rootNodes)[0]>(predicate: (value: T, index: number, array: T[]) => boolean) {
        return this.getAllNodes().filter(predicate);
    }

    /**
     * Gets the specified node and any of its descendant nodes as a flat array.
     * @param node The node to check.
     * @returns An array of all descendant nodes, including the original `node`.
     */
    getSelfAndDescendants(node: Node<any>): Node<any>[] {
        let nodes: Node<any>[] = [];

        if (node) {
            nodes.push(node);
            nodes.push(...(node.children?.flatMap((c) => this.getSelfAndDescendants(c)) ?? []));
        }

        return nodes;
    }

    /**
     * Whether the node is currently selected.
     * @param node The node to check.
     * @returns `true` if selected.
     */
    isNodeSelected(node: TypedNode): boolean {
        return node && this.selectedNodeIds.has(node.id);
    }

    /**
     * Whether the node is currently disabled (and cannot be selected)
     * @param node The node to check.
     * @returns `true` if disabled.
     */
    isNodeDisabled(node: Node<any>): boolean {
        return !!node.data?.disabled;
    }

    /**
     * Whether the node is currently expanded.
     * @param node The node to check.
     * @returns `true` if expanded.
     */
    isNodeExpanded(node: Node<any>): boolean {
        return node && this.expandedNodeIds.has(node.id);
    }

    private setupDetailsBox() {
        this.context.map.detailsBox = {
            component: "omnia-fields-map-cropping-details",
            isVisible: () => this.context.map && this.svc.selectedCroppings.length > 0,
            context: <IFieldsMapCroppingDetailsContext>{
                map: this.context.map,
                getSelectedCroppings: () => this.svc.selectedCroppings,
                commands: new OmniaCommandGroup(str.Cropping, undefined, getCroppingCommandsForDetailBox(this.context)),
            },
        };
    }
}

/**
 * Helper function to parent child nodes.
 * @param parent The parent node to populate.
 * @param children The child nodes to attach/populate.
 * @returns The parent node, with a populated `children` property, where each child has a populated `parent` property.
 */
function withChildren<T extends Omit<TypedNode, "children">>(
    parent: T,
    children: TypedNode[]
): T & { children: TypedNode[] } {
    return {
        ...parent,
        children: children.map((child) => ({
            ...child,
            parent: parent.id,
        })),
    };
}

function groupByField(items: FieldViewModel[]) {
    const groups = groupBy(
        items,
        (item) => item.fieldVm,
        (key) => key?.fieldId
    );

    return sortBy(groups, [(x) => x.key?.disabled, (x) => x.key?.fieldName, (x) => x.key?.fieldId, (x) => x.key]);
}

function groupByVariety(items: FieldViewModel[]) {
    const groups = groupBy(
        items,
        (item) => item.variety,
        (key) => key?.CropVarietyId
    );

    return sortBy(groups, [
        (x) => x.key?.Crop?.Name,
        (x) => x.key?.CropId,
        (x) => x.key?.Name,
        (x) => x.key?.CropVarietyId,
        (x) => x.key,
    ]);
}

function groupByPurpose(items: FieldViewModel[]) {
    const groups = groupBy(items, (item) => item.marketId as CropPurpose);

    return sortBy(groups, [(x) => (x.key ? labels.CropPurpose(x.key) : undefined)]);
}

function groupByCrop(items: FieldViewModel[]) {
    const groups = groupBy(
        items,
        (item) => item.crop,
        (key) => key?.CropId
    );

    return sortBy(groups, [(x) => x.key?.Name, (x) => x.key?.CropId, (x) => x.key]);
}

function groupByFieldGroup(items: FieldViewModel[]) {
    return groupBy(items, (item) => item.fieldGroup);
}

function groupByCropping(items: FieldViewModel[]) {
    const groups = groupBy(
        items,
        (item) => item,
        (key) => key.croppingId
    );

    return sortBy(groups, [
        (x) => x.key?.fieldName,
        (x) => x.key?.crop?.Name,
        (x) => x.key?.cropId,
        (x) => x.key?.variety?.Name,
        (x) => x.key?.varietyId,
        (x) => x.key?.cropping?.PlotId,
        [(x) => x.key?.area, "desc"],
        (x) => x.key?.croppingId,
        (x) => x.key,
    ]);
}

function asCroppingNodes(items: FieldViewModel[], onSelect: (cropping: FieldViewModel) => void) {
    return groupByCropping(items)
        .filter((x) => x.key)
        .map<CroppingNode>(({ key: cropping }) => ({
            dataType: "cropping",
            data: cropping,
            id: nodeId(),
            onSelect: () => onSelect(cropping),
        }));
}

function makeGroup<K, V extends FieldViewModel>(
    groupFn: (items: V[]) => { key: K; values: V[] }[],
    groupNodeFn: (group: { key: K }) => Omit<TypedNode, "children" | "id">
): (items: V[], mapChildren: (items: FieldViewModel[]) => TypedNode[]) => TypedNode[] {
    return (items, mapChildren) => {
        const visibleItems = items.filter((x) => x.visible);
        const groups = groupFn(visibleItems);
        if (groups.length > 1) {
            return groups.map(({ key, values }) =>
                withChildren(
                    {
                        id: nodeId(),
                        ...groupNodeFn({ key }),
                    },
                    mapChildren(values)
                )
            ) as TypedNode[];
        } else {
            return mapChildren(visibleItems) as TypedNode[];
        }
    };
}

const maybeGroupCrop = makeGroup(groupByCrop, (x) => ({ dataType: "crop", data: x.key }));

const maybeGroupVariety = makeGroup(groupByVariety, (x) => ({ dataType: "variety", data: x.key }));

const maybeGroupPurpose = makeGroup(groupByPurpose, (x) => ({ dataType: "purpose", data: x.key }));
